"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from "@/lib/utils";
import DownloadDialog from "@/components/Library/DownloadDialog";
import NPdfDialog from "@/components/Library/NPdfDialog";
import { Icon, InteractionLayer, Typography } from "@bookips/sds";
import * as Accordion from "@radix-ui/react-accordion";
import { forwardRef, useEffect } from "react";
import { mixpanelEvent } from "src/lib/mixpanels/mixpanel";
import { ProductListHeader } from "./ProductListHeader";
import { ProductListItem, ProductListItemSkeleton } from "./ProductListItem";
import { useProductItemHandlers } from "./useProductItemHandlers";
const AccordionTrigger = /*#__PURE__*/ forwardRef((param, ref)=>{
    let { title , count , className  } = param;
    return /*#__PURE__*/ _jsx(Accordion.Header, {
        asChild: true,
        children: /*#__PURE__*/ _jsx(Accordion.Trigger, {
            asChild: true,
            children: /*#__PURE__*/ _jsxs(InteractionLayer, {
                ref: ref,
                as: "div",
                variant: "light",
                className: cn(// 모바일: px-4 (부모 -mx-4 상쇄), sm 이상: px-5
                "group flex w-full cursor-pointer items-center justify-between px-4 py-3 bg-white data-[state=open]:bg-gray-0 sm:px-5", className),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "subtitle1",
                                className: "text-primary sm:hidden",
                                children: title
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "h6",
                                className: "hidden text-primary sm:block",
                                children: title
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                variant: "caption",
                                className: "text-secondary sm:hidden",
                                children: [
                                    count,
                                    "개"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                variant: "body2",
                                className: "hidden text-secondary sm:block",
                                children: [
                                    count,
                                    "개"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex size-6 shrink-0 items-center justify-center rounded sm:size-8",
                        children: /*#__PURE__*/ _jsx(Icon.ArrowDown_2px, {
                            className: "size-[18px] text-gray-700 transition-transform duration-200 group-data-[state=open]:rotate-180 sm:size-5"
                        })
                    })
                ]
            })
        })
    });
});
AccordionTrigger.displayName = "AccordionTrigger";
const AccordionContent = (param)=>{
    let { items , createSampleClickHandler , createCtaClickHandler , createPdfDownloadHandler , getAcquiredLicenseInfo  } = param;
    return /*#__PURE__*/ _jsx(Accordion.Content, {
        className: "overflow-hidden bg-white data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
        children: /*#__PURE__*/ _jsx("div", {
            className: "flex flex-col",
            children: items.map((item, index)=>/*#__PURE__*/ _jsx(ProductListItem, {
                    product: item,
                    className: cn(// 모바일: px-5 (부모 -mx-5 상쇄), sm 이상: p-5
                    "px-5 py-5 sm:p-5", index < items.length - 1 && "border-b border-gray-100"),
                    onSampleClick: createSampleClickHandler(item),
                    onCtaClick: createCtaClickHandler(item),
                    onPdfDownloadClick: createPdfDownloadHandler(item),
                    acquiredLicenseInfo: getAcquiredLicenseInfo(item.id)
                }, item.id))
        })
    });
};
/**
 * 빈 상태 컴포넌트 - Mixpanel 이벤트 트래킹 포함
 */ const EmptyState = (param)=>{
    let { message  } = param;
    useEffect(()=>{
        mixpanelEvent("No content to display", {
            position: "에디션 카테고리"
        });
    }, []);
    return /*#__PURE__*/ _jsx("div", {
        className: "flex items-center justify-center py-12",
        children: /*#__PURE__*/ _jsx("p", {
            className: "text-gray-500",
            children: message
        })
    });
};
export const AccordionProductList = (param)=>{
    let { className , groups , totalCount , fixedSortLabel ="단원순" , defaultExpandedIds , isLoading , emptyMessage ="상품이 없습니다." , purchaseUrl  } = param;
    // 기본 펼침 상태 결정:
    // 1. defaultExpandedIds가 유효하게 전달되면 사용
    // 2. 단원이 1개이고 아이템도 1개인 경우 해당 단원 펼침
    // 3. 그 외에는 첫 번째 그룹만 펼침
    const getDefaultExpandedIds = ()=>{
        // defaultExpandedIds가 유효한 배열이면 사용 (빈 배열은 무시)
        if (defaultExpandedIds && defaultExpandedIds.length > 0) {
            return defaultExpandedIds;
        }
        if (groups.length === 0) return [];
        // 단원이 1개이고 아이템도 1개인 경우
        if (groups.length === 1 && groups[0].items.length === 1) {
            return [
                groups[0].id
            ];
        }
        return [
            groups[0].id
        ];
    };
    const defaultValue = getDefaultExpandedIds();
    // 공통 핸들러 훅 사용
    const { createSampleClickHandler , createCtaClickHandler , createPdfDownloadHandler , isDownloadDialogOpen , downloadData , isFirstUseForDownload , closeDownloadDialog , handleDownloadComplete , getAcquiredLicenseInfo , nPdfOpen , closeNPdfDialog , nPdfCount , nPdfActionType , handleNPdfDirectAction , handleNPdfGoToDetail , nPdfDetailUrl  } = useProductItemHandlers({
        purchaseUrl
    });
    return /*#__PURE__*/ _jsxs("section", {
        className: cn("mb-6 flex flex-col", className),
        children: [
            /*#__PURE__*/ _jsx(ProductListHeader, {
                totalCount: totalCount,
                fixedSortLabel: fixedSortLabel
            }),
            isLoading ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col sm:gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: cn("animate-pulse overflow-hidden", "max-sm:-mx-5 border-b border-gray-700", "sm:mx-0 sm:rounded-md sm:border"),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between bg-gray-0 px-5 py-3 sm:px-5",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-1.5 sm:gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-5 w-14 rounded bg-gray-200 sm:h-6 sm:w-16"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-4 w-7 rounded bg-gray-100 sm:h-5 sm:w-8"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "size-6 rounded bg-gray-100 sm:size-8"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "bg-white",
                                children: Array.from({
                                    length: 3
                                }).map((_, itemIndex)=>/*#__PURE__*/ _jsx(ProductListItemSkeleton, {
                                        variant: "accordion",
                                        className: cn("px-5 py-5 sm:p-5", itemIndex < 2 && "border-b border-gray-100")
                                    }, itemIndex))
                            })
                        ]
                    }),
                    Array.from({
                        length: 2
                    }).map((_, index)=>/*#__PURE__*/ _jsx("div", {
                            className: cn("animate-pulse overflow-hidden", "max-sm:-mx-5 border-b border-gray-200", "sm:mx-0 sm:rounded-md sm:border"),
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between px-5 py-3 sm:px-5",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-1.5 sm:gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-5 w-16 rounded bg-gray-200 sm:h-6 sm:w-20"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-4 w-7 rounded bg-gray-100 sm:h-5 sm:w-8"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "size-6 rounded bg-gray-100 sm:size-8"
                                    })
                                ]
                            })
                        }, index))
                ]
            }) : groups.length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                message: emptyMessage
            }) : /*#__PURE__*/ _jsx(Accordion.Root, {
                type: "multiple",
                defaultValue: defaultValue,
                className: "flex flex-col sm:gap-3",
                children: groups.map((group)=>/*#__PURE__*/ _jsxs(Accordion.Item, {
                        value: group.id,
                        className: cn(// 모바일: negative margin으로 border를 전체 너비로 확장 (px 없음 - 내부에서 처리)
                        "max-sm:-mx-5 overflow-hidden border-b border-gray-200 data-[state=open]:border-gray-700", // sm 이상: margin 리셋, rounded + 전체 border
                        "sm:mx-0 sm:rounded-md sm:border sm:data-[state=open]:border-gray-700"),
                        children: [
                            /*#__PURE__*/ _jsx(AccordionTrigger, {
                                title: group.title,
                                count: group.items.length
                            }),
                            /*#__PURE__*/ _jsx(AccordionContent, {
                                items: group.items,
                                createSampleClickHandler: createSampleClickHandler,
                                createCtaClickHandler: createCtaClickHandler,
                                createPdfDownloadHandler: createPdfDownloadHandler,
                                getAcquiredLicenseInfo: getAcquiredLicenseInfo
                            })
                        ]
                    }, group.id))
            }, groups.map((g)=>g.id).join("-")),
            downloadData && /*#__PURE__*/ _jsx(DownloadDialog, {
                isOpen: isDownloadDialogOpen,
                close: closeDownloadDialog,
                selectedData: downloadData,
                selectedFileType: "pdf",
                onDownloadComplete: handleDownloadComplete,
                getOpenViewer: {
                    isFirstUse: isFirstUseForDownload
                },
                skipConfirm: !isFirstUseForDownload,
                position: "에디션 카테고리"
            }),
            /*#__PURE__*/ _jsx(NPdfDialog, {
                open: nPdfOpen,
                onOpenChange: (open)=>{
                    if (!open) closeNPdfDialog();
                },
                count: nPdfCount !== null && nPdfCount !== void 0 ? nPdfCount : 0,
                actionType: nPdfActionType,
                onDirectAction: handleNPdfDirectAction,
                onGoToDetail: handleNPdfGoToDetail,
                position: "에디션 카테고리",
                detailUrl: nPdfDetailUrl
            })
        ]
    });
};
